import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { addNewDropdownItem } from '../../data-entry-form/services/addNewDropdownService';
import { addNewAbbreviationItem } from '../../data-entry-form/services/abbreviationsService';
import { getAuthenticatedAccount } from '../../utils/accountUtils';

const dropdownTypes = [
  { value: 'title', label: 'Author' },
  { value: 'addressname', label: 'Address Name' },
  { value: 'sitename1', label: 'Site Names' },
  { value: 'sitename2', label: 'Site Names' },
  { value: 'sitename3', label: 'Site Names' }
];

const AddNewDropdownValue = ({ open, onClose, onSubmit, instance, accounts, tenantId, fieldName }) => {
  const [newDropdownValue, setNewDropdownValue] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [abbreviation, setAbbreviation] = React.useState('');
  const [selectedType, setSelectedType] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [showSuccessToast, setShowSuccessToast] = React.useState(false);

  React.useEffect(() => {
    if (fieldName) {
      setSelectedType(fieldName);
    }
  }, [fieldName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');
    setIsSubmitting(true);

    try {
      const account = await getAuthenticatedAccount(instance, accounts, tenantId);
      
      if (!account) {
        throw new Error('Authentication failed. Please try logging in again.');
      }

      if (selectedType === 'sitename1' || selectedType === 'sitename2' || selectedType === 'sitename3') {
        const requests = [
          {
            Title: 'sitename1',
            field_1: newDropdownValue,
            field_2: description
          },
          {
            Title: 'sitename2',
            field_1: newDropdownValue,
            field_2: description
          },
          {
            Title: 'sitename3',
            field_1: newDropdownValue,
            field_2: description
          }
        ];
        await Promise.all(requests.map(payload => 
          addNewDropdownItem(instance, accounts, tenantId, payload)
        ));
      } else {
        const payload = {
          Title: selectedType,
          field_1: newDropdownValue,
          field_2: description,
          ...(selectedType === 'title' && { field_3: abbreviation })
        };

        await addNewDropdownItem(instance, accounts, tenantId, payload);

        // If the type is "title" (author), also add to abbreviations list
        if (selectedType === 'title' && abbreviation) {
          const abbreviationPayload = {
            Title: "title",
            field_1: newDropdownValue,
            field_2: abbreviation,
            field_3: "Author Abbreviation"
          };
          
          await addNewAbbreviationItem(instance, accounts, tenantId, abbreviationPayload);
        }
      }

      onSubmit({ name: newDropdownValue, description, type: selectedType, abbreviation });
      setNewDropdownValue('');
      setDescription('');
      setAbbreviation('');
      setSelectedType('');
      setShowSuccessToast(true);
      onClose();
    } catch (err) {
      let errorMessage = 'Failed to add dropdown value. ';
      if (err.message.includes('No accounts available')) {
        errorMessage += 'Please ensure you are logged in.';
      } else if (err.message.includes('Authentication failed')) {
        errorMessage = err.message;
      } else {
        errorMessage += 'Please try again.';
      }
      setError(errorMessage);
      console.error('Error adding dropdown value:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setNewDropdownValue('');
    setDescription('');
    setAbbreviation('');
    setSelectedType('');
    setError('');
    onClose(fieldName);
  };

  const handleCloseToast = () => {
    setShowSuccessToast(false);
  };

  React.useEffect(() => {
    if (open && (!instance || !accounts || !tenantId)) {
      setError('Missing required authentication information. Please ensure you are logged in.');
    }
  }, [open, instance, accounts, tenantId]);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit} noValidate>
          <DialogTitle>Add New Dropdown Value</DialogTitle>
          <DialogContent>
            {error && (
              <div style={{ color: 'red', marginBottom: '10px' }}>
                {error}
              </div>
            )}
            <FormControl fullWidth margin="dense">
              <InputLabel>Type</InputLabel>
              <Select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                required
                disabled={isSubmitting}
                label="Type"
                readOnly={true}
              >
                {dropdownTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              label="Dropdown Value"
              type="text"
              fullWidth
              value={newDropdownValue}
              onChange={(e) => setNewDropdownValue(e.target.value)}
              required
              disabled={isSubmitting}
            />
            {selectedType === 'title' && (
              <TextField
                margin="dense"
                label="Abbreviation"
                type="text"
                fullWidth
                value={abbreviation}
                onChange={(e) => setAbbreviation(e.target.value)}
                disabled={isSubmitting}
              />
            )}
            <TextField
              margin="dense"
              label="Description"
              type="text"
              fullWidth
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSubmitting}
            />
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button 
              type="submit"
              variant="contained" 
              color="primary"
              disabled={isSubmitting || !instance || !accounts || !tenantId || !selectedType}
            >
              {isSubmitting ? 'Adding...' : 'Add Dropdown Value'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Snackbar
        open={showSuccessToast}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        message="Dropdown value successfully added"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default AddNewDropdownValue; 